import { config } from '@/config/environment';

type TextString = {
  title?: string;
  subtitle?: string;
  text?: string;
};

const liveTextStrings: Record<string, TextString> = {
  shareWhatYouLove: {
    title: 'Share what you love',
    subtitle: 'become a host to...',
    text: `At Curiko, we think everyone is interesting. From anime to zoology, all of us have a curiosity, a hobby, a fascination, something to offer—even if we don't know what, yet. We can help awaken your (hidden) passions and exchange them with others. Reciprocity makes life so much richer.`,
  },
  doSomethingGood: {
    title: 'Gain Perspective',
    subtitle: 'become a host to...',
    text: `Do you find you're in a bubble—interacting with the same kinds of folks, day after day? Curiko can help widen your viewfinder and see fellow humans differently. We support your curiosity and learning with reflection tools, check-ins, and feedback.`,
  },
  challengeYourPerspective: {
    title: 'Learn and grow',
    subtitle: 'become a host to...',
    text: `Curiko is the easiest way to volunteer. Share your interests online, at a coffee shop, or wherever you want, at a time that works for you! Spend an hour once a month hosting an experience one-on-one or in a group, with a video or box. It's super flexible and completely shaped by you.
      `,
  },
  shareWhatYouLove1: {
    title: 'Set your own schedule',
    subtitle: 'become a host to...',
    text: `Curiko is the easiest way to volunteer. Share your interests online, at a coffee shop, or wherever you want, at a time that works for you! Spend an hour once a month hosting an experience one-on-one or in a group, with a video or box. It's super flexible and completely shaped by you.
      `,
  },
  bePartOfAnEquitableFuture: {
    title: 'Be part of the change you want to see',
    subtitle: 'become a host to...',
    text: `Connect with people whose bodies and brains work in beautifully different ways. Challenge your biases and assumptions. Be part of creating a just and thriving community.`,
  },
};

const archiveTextStrings: Record<string, TextString> = {
  shareWhatYouLove: {
    title: 'Share what you love',
    subtitle: '',
    text: "At Curiko, we believed everyone had something interesting to share. From anime to zoology, people brought their curiosities, hobbies, and passions—even if they hadn't fully discovered them yet. Hosting helped uncover hidden interests and created opportunities to exchange them with others. Reciprocity made life richer.",
  },
  doSomethingGood: {
    title: 'Gain Perspective',
    subtitle: '',
    text: 'Curiko offered people a chance to step outside familiar circles and connect with people they might never have otherwise met. Through conversations, reflection tools, check-ins, and feedback, Hosts broadened their perspectives while helping others do the same.',
  },
  challengeYourPerspective: {
    title: 'Learn and grow',
    subtitle: '',
    text: 'Hosting became an opportunity to learn alongside others. People shared their interests online, in cafés, or wherever felt right while discovering new perspectives through every conversation. Experiences were flexible, shaped by Hosts, and designed to fit into everyday life.',
  },
  shareWhatYouLove1: {
    title: 'Set your own schedule',
    subtitle: '',
    text: "Hosting fit around people's lives. Experiences could take place online, in cafés, or wherever felt right, at a time that worked for each Host. Whether one-on-one or with a group, experiences remained flexible and were shaped around individual schedules.",
  },
  bePartOfAnEquitableFuture: {
    title: 'Be part of the change you want to see',
    subtitle: '',
    text: 'Hosting brought together people whose bodies, minds, and lived experiences were different. Along the way, Hosts challenged assumptions, built meaningful relationships, and helped create a more connected and inclusive community.',
  },
};

export const textStrings = config.platformMode === 'archive' ? archiveTextStrings : liveTextStrings;

type Process = {
  img?: string;
  name?: string;
};
const liveProcesses: Process[] = [
  {
    img: '/discover/hosting/step1.png',
    name: 'Brainstorm an idea: what do you want to share?',
  },
  {
    img: '/discover/hosting/step2.png',
    name: 'Shape your experience with one of our helpful Curators',
  },
  {
    img: '/discover/hosting/step3.png',
    name: 'Test drive your experience before it goes live',
  },
  {
    img: '/discover/hosting/step4.png',
    name: 'Host your experience at the time you set',
  },
];

export const processes = liveProcesses;

type Reason = {
  mainIcon?: string;
  heading?: string;
  detail?: string;
};

const liveReasons: Record<string, Reason> = {
  reason1: {
    mainIcon: '/discover/hosting/mobile/reason1.png',
    heading: 'Share what you love',
    detail: `At Curiko, we think everyone is interesting. From anime to zoology, all of us have a curiosity, a hobby, a fascination, something to offer—even if we don't know what, yet. We can help awaken your (hidden) passions and exchange them with others. Reciprocity makes life so much richer.`,
  },
  reason2: {
    mainIcon: '/discover/hosting/mobile/reason2.png',
    heading: 'Set your own schedule',
    detail: `Curiko is the easiest way to volunteer. Share your interests online, at a coffee shop, or wherever you want, at a time that works for you! Spend an hour once a month hosting an experience one-on-one or in a group, with a video or box. It's super flexible and completely shaped by you.`,
  },
  reason3: {
    mainIcon: '/discover/hosting/mobile/reason3.png',
    heading: 'Gain perspective',
    detail: `Do you find you're in a bubble—interacting with the same kinds of folks, day after day? Curiko can help widen your viewfinder and see fellow humans differently. We support your curiosity and learning with reflection tools, check-ins, and feedback.`,
  },
  reason4: {
    mainIcon: '/discover/hosting/mobile/reason4.png',
    heading: 'Learn and grow',
    detail: `Practice your relational skills. Diversify how you communicate. Appreciate new things about who you are and what you are capable of. Earn badges for stretching yourself!`,
  },
  reason5: {
    mainIcon: '/discover/hosting/mobile/reason5.png',
    heading: 'Be part of the change you want to see',
    detail: `Connect with people whose bodies and brains work in beautifully different ways. Challenge your biases and assumptions. Be part of creating a just and thriving community.`,
  },
};

const archiveReasons: Record<string, Reason> = {
  reason1: {
    mainIcon: '/discover/hosting/mobile/reason1.png',
    heading: 'Share what you love',
    detail:
      'Community members hosted experiences to share what they loved, gain new perspectives, and grow their networks. Through Curiko, they contributed their passions and connected with others on equal ground. Together, they explored what it could look like to build two-way relationships and more inclusive communities.',
  },
  reason2: {
    mainIcon: '/discover/hosting/mobile/reason2.png',
    heading: 'Set your own schedule',
    detail:
      "Hosting fit around people's lives. Experiences could take place online, in cafés, or wherever felt right, at a time that worked for each Host. Whether one-on-one or with a group, experiences remained flexible and were shaped around individual schedules.",
  },
  reason3: {
    mainIcon: '/discover/hosting/mobile/reason3.png',
    heading: 'Gain perspective',
    detail:
      'Curiko offered people a chance to step outside familiar circles and connect with people they might never have otherwise met. Through conversations, reflection tools, check-ins, and feedback, Hosts broadened their perspectives while helping others do the same.',
  },
  reason4: {
    mainIcon: '/discover/hosting/mobile/reason4.png',
    heading: 'Learn and grow',
    detail:
      'Hosting became an opportunity to learn alongside others. People shared their interests online, in cafés, or wherever felt right while discovering new perspectives through every conversation. Experiences were flexible, shaped by Hosts, and designed to fit into everyday life.',
  },
  reason5: {
    mainIcon: '/discover/hosting/mobile/reason5.png',
    heading: 'Be part of the change you want to see',
    detail:
      'Hosting brought together people whose bodies, minds, and lived experiences were different. Along the way, Hosts challenged assumptions, built meaningful relationships, and helped create a more connected and inclusive community.',
  },
};

export const reasons = config.platformMode === 'archive' ? archiveReasons : liveReasons;
